// MARK://dhk8/pos/features/pos/BottomSheet
// Lightweight bottom-sheet primitive for the 534×738 tablet-portrait POS.
// A scrim + a panel that slides up from the bottom and occupies a configurable
// fraction of the viewport height. Drag-handle + backdrop tap close. Theme-aware
// via --pos-* tokens. No external deps. Used by OrderPage's cart sheet.

import { useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface BottomSheetProps {
  open: boolean
  onClose: () => void
  /** Sheet title, shown next to the drag handle row. */
  title?: React.ReactNode
  /** Accessible name for the dialog (use when `title` is a node, not plain text). */
  ariaLabel?: string
  /** Fraction of viewport height the sheet occupies (0–1). Default 0.78. */
  heightFraction?: number
  children: React.ReactNode
  /** Optional footer pinned to the bottom of the sheet (totals + actions). */
  footer?: React.ReactNode
  /** When true, the sheet behaves as a centered modal on desktop (≥1024px) — a
   *  capped-width dialog with a backdrop, no slide-up. On mobile it still
   *  renders as the bottom-sheet drawer. Default false (existing behaviour). */
  centerOnDesktop?: boolean
}

export default function BottomSheet({ open, onClose, title, ariaLabel, heightFraction = 0.78, children, footer, centerOnDesktop = false }: BottomSheetProps) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  // Portal to <body> so the sheet escapes any ancestor stacking context (the
  // nav rail / topbar create their own on iOS Safari and would otherwise win
  // the layering). Renders on top of everything via --pos-z-sheet.
  const node = (
    <div className={`dhk8-bsheet${centerOnDesktop ? ' dhk8-bsheet--center-desktop' : ''}`} role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <button type="button" className="dhk8-bsheet__scrim" aria-label="Cerrar" onClick={onClose} />
      <div className="dhk8-bsheet__panel" style={{ maxHeight: `${Math.round(heightFraction * 100)}vh` }}>
        <div className="dhk8-bsheet__grip" onClick={onClose}>
          <span className="dhk8-bsheet__handle" aria-hidden />
        </div>
        {title !== undefined && <div className="dhk8-bsheet__title">{title}</div>}
        <div className="dhk8-bsheet__body">{children}</div>
        {footer !== undefined && <div className="dhk8-bsheet__foot">{footer}</div>}
      </div>
      <style>{styles}</style>
    </div>
  )
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node
}

const styles = `
  .dhk8-bsheet { position:fixed; inset:0; z-index:var(--pos-z-sheet, 200); display:flex; flex-direction:column; justify-content:flex-end; }
  .dhk8-bsheet__scrim { position:absolute; inset:0; z-index:var(--pos-z-sheet-scrim, 199); background:var(--pos-overlay, rgba(8,12,10,.72)); border:none; padding:0; margin:0; cursor:pointer; animation:dhk8-bsheet-fade var(--pos-duration-fast,120ms) ease; }
  @keyframes dhk8-bsheet-fade { from { opacity:0 } to { opacity:1 } }
  .dhk8-bsheet__panel { position:relative; z-index:var(--pos-z-sheet, 200); display:flex; flex-direction:column; min-height:0; background:var(--pos-surface, #161C18); border-top:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-xl,16px) var(--pos-radius-xl,16px) 0 0; box-shadow:var(--pos-shadow-4, 0 -24px 60px -16px rgba(0,0,0,.55)); animation:dhk8-bsheet-up var(--pos-duration-base,200ms) cubic-bezier(.2,.8,.2,1); }
  @keyframes dhk8-bsheet-up { from { transform:translateY(100%) } to { transform:translateY(0) } }
  .dhk8-bsheet__grip { display:flex; align-items:center; justify-content:center; padding:var(--pos-space-2,8px) 0 var(--pos-space-1,4px); cursor:grab; flex-shrink:0; }
  .dhk8-bsheet__handle { width:40px; height:4px; border-radius:var(--radius-full); background:var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-bsheet__title { flex-shrink:0; padding:0 var(--pos-space-4,16px) var(--pos-space-2,8px); font-family:var(--font-display, system-ui); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-bsheet__body { flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
  .dhk8-bsheet__foot { flex-shrink:0; border-top:1px solid var(--pos-separator, rgba(255,255,255,.07)); background:var(--pos-elevated, #1E2520); }

  /* Desktop modal mode: only kicks in when the caller passed centerOnDesktop. The
     sheet keeps drawer behaviour on mobile/tablet-portrait; from 1024px up the
     overlay centers a capped-width dialog over the page (Menu product detail). */
  @media (min-width: 1024px) {
    .dhk8-bsheet--center-desktop { justify-content:center; align-items:center; padding:var(--pos-space-5,20px); }
    .dhk8-bsheet--center-desktop .dhk8-bsheet__panel { width:min(560px, 100%); max-width:560px; max-height:min(86vh, 760px) !important; border-radius:var(--pos-radius-xl,16px); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); animation:dhk8-bsheet-pop var(--pos-duration-base,200ms) cubic-bezier(.2,.8,.2,1); }
    @keyframes dhk8-bsheet-pop { from { transform:scale(.96); opacity:0 } to { transform:scale(1); opacity:1 } }
    /* Drag handle is meaningless when not a sheet — collapse it. */
    .dhk8-bsheet--center-desktop .dhk8-bsheet__grip { display:none; }
    .dhk8-bsheet--center-desktop .dhk8-bsheet__title { padding:var(--pos-space-4,16px) var(--pos-space-4,16px) var(--pos-space-2,8px); }
  }
`
